import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { DateTime } from 'luxon';

import MicrosoftContext from './context';
import { MicrosoftValue, TokenDataType, UserType } from './types';
import { AuthenticationResult } from '@azure/msal-browser';

const APP_PREFIX = '__radian__';

const localStorageKeys = {
  BEARER_TOKEN: `${APP_PREFIX}bearer_token`,
  GRAPH_USER_ID: `${APP_PREFIX}graph_user_id`,
  PURCHASE_TOKEN: `${APP_PREFIX}token`,
  PURCHASE_TOKEN_CREATED_AT: `${APP_PREFIX}token_createdAt`,
};

function fetchPlanDataHelper (subscriptionId: string, cb: (plans: any) => void) {
  axios
    .get(`/apis/microsoft/subscriptions/fetch_plans/${subscriptionId}/`)
    .then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK') {
          if (res.data.plans) {
            const plans = res.data.plans;
            // Remove plans that shouldn't be sold in the Marketplace
            const _plans = _.filter(plans, (p) => { return !p.isStopSell; });
            cb(_plans);
          } else {
            console.warn('<MicrosoftProvider>: No plan data returned.');
            cb([]);
          }
        } else {
          console.warn(`<MicrosoftProvider>: Could not access plan data. ${res.data.message}.`);
          cb([]);
        }
      },
      (err: AxiosError) => {
        console.error(`<MicrosoftProvider>: Error fetching plan data. ${err.status}: ${err.message}.`);
        cb([]);
      },
    );
}

function fetchUserData (bearerToken: AuthenticationResult, cb: (user: any) => void) {
  axios
    .post('/apis/microsoft/auth/graph/call_graph_api/', {
      accessToken: bearerToken.accessToken,
    })
    .then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK' && res.data.user) {
          const user = res.data.user;
          window.sessionStorage.setItem(localStorageKeys.GRAPH_USER_ID, user.id);
          cb(user);
        } else {
          console.error(res.data);
        }
        return;
      },
      (err: AxiosError) => {
        console.error(err);
        return;
      },
    );
}

function fetchPurchaseToken (cb: (data: any) => void) {
  const data = {
    token: window.localStorage.getItem(localStorageKeys.PURCHASE_TOKEN),
    token_created_at: window.localStorage.getItem(localStorageKeys.PURCHASE_TOKEN_CREATED_AT),
  };
  if (data.token && data.token_created_at) {
    const currentTime = DateTime.now();
    const storedTime = DateTime.fromISO(data.token_created_at);
    const diff = currentTime.diff(storedTime, 'hours').toObject().hours;
    // Microsoft purchase tokens expire after 24 hours. This is a rough, conservative test.
    if (diff < 23) {
      cb(data);
      return;
    }
  }
  cb(null);
  return;
}

async function fetchBearerToken (cb: (bearer: any) => void) {
  // Check if token exists
  const bearer: AuthenticationResult = JSON.parse(window.sessionStorage.getItem(localStorageKeys.BEARER_TOKEN));
  if (bearer) {
    // Check if it has expired
    const bearerExpiry = DateTime.fromJSDate(new Date(bearer.expiresOn));
    const currentTime = DateTime.now();
    const diff = currentTime.diff(bearerExpiry, 'milliseconds').toObject().milliseconds;
    if (diff < 0) {
      cb(bearer);
      return;
    }
  }
  cb(null);
}

function _handleSSO (
  error: any,
  type: UserType)
{
  axios
    .get(`/apis/microsoft/auth/url_for_${type}/`)
    .then(
      (res: AxiosResponse) => {
        if (res.data.status === 'OK' && res.data.url) {
          window.location.assign(res.data.url);
        } else {
          error(res.data);
        }
      },
      (err: AxiosError) => {
        error(err);
      }
    );
}

const MicrosoftProvider = (props): JSX.Element => {
  const [azureIdentity, setAzureIdentity] = useState();
  const [subscriberTenantId, setSubscriberTenantId] = useState('');
  
  const [planData, setPlanData] = useState();

  // Microsoft graph data aoubt the user via https://graph.microsoft.com/v1.0/me
  const [graphData, setGraphData] = useState();

  // Purchase identification token sent by Azure marketplace via ?token=
  const [purchaseToken, setPurchaseToken] = useState<TokenDataType>();
  
  // Bearer token returned from /apis/microsoft/auth/token_for_subscriber
  const [bearerToken, setBearerToken] = useState<AuthenticationResult>();
  
  // If has bearerToken, user is officially authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPublisher, setIsPublisher] = useState(false);

  const [error, setError] = useState();

  useEffect(() => {
    fetchPurchaseToken(setPurchaseToken);
    fetchBearerToken(setBearerToken);
  }, []);

  useEffect(() => {
    if (bearerToken) {
      setIsAuthenticated(true);
      fetchUserData(bearerToken, setGraphData);
    }
  }, [bearerToken]);

  function fetchPlanData (subscriptionId: string) {
    fetchPlanDataHelper(subscriptionId, setPlanData);
  }

  function resetPlanData () {
    setPlanData(undefined);
  }

  function storePurchaseToken (token: string) {
    const token_created_at = DateTime.now().toISO();
    if (purchaseToken && purchaseToken.token) {
      // Replace current token
      if (token !== purchaseToken.token) {
        window.localStorage.setItem(localStorageKeys.PURCHASE_TOKEN, token);
        window.localStorage.setItem(localStorageKeys.PURCHASE_TOKEN_CREATED_AT, token_created_at);
      } else {
        console.info(`<MicrosoftProvider/> Token ${token} already exists and is still valid.`);
        return;
      }
    } else {
      // Store new token
      window.localStorage.setItem(localStorageKeys.PURCHASE_TOKEN, token);
      window.localStorage.setItem(localStorageKeys.PURCHASE_TOKEN_CREATED_AT, token_created_at);
    }
    setPurchaseToken({
      token,
      token_created_at,
    });
  }

  function _setSubscriberTenantId(tenantId: string) {
    setSubscriberTenantId(tenantId);
  }

  function _setBearerToken(bearerToken: AuthenticationResult) {
    window.sessionStorage.setItem(localStorageKeys.BEARER_TOKEN, JSON.stringify(bearerToken));
    setBearerToken(bearerToken);
    setIsAuthenticated(true);
  }

  function handleSSO (type: UserType) {
    _handleSSO(setError, type);
  }

  const value: MicrosoftValue = {
    azureIdentity,
    planData,
    purchaseToken,
    fetchPlanData,
    subscriberTenantId,
    setPurchaseToken: storePurchaseToken,
    setSubscriberTenantId: _setSubscriberTenantId,
    resetPlanData,
    handleSSO,
    isAuthenticated,
    isPublisher,
    setAzureIdentity,
    setBearerToken: _setBearerToken,
    setIsAuthenticated,
    setIsPublisher,
    bearerToken,
    error,
    graphData,
  };
  return (
    <MicrosoftContext.Provider value={value} {...props} />
  );
};

export default MicrosoftProvider;
